import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import {
  Menu, X, Search, Bell, ChevronDown, ChevronRight, LogOut, LayoutDashboard,
  ShieldCheck, PanelLeftClose, PanelLeftOpen,
} from "lucide-react";
import { ccClient, ccToken, ccErrorInfo } from "./lib/ccClient";
import { adminClient } from "../lib/admin";
import { NAV_GROUPS, resolveItemStatus } from "./lib/registry";
import { ModuleStatusBadge } from "./components/primitives";
import { OverviewPanel, CommercePanel, FinancialPanel, OrdersPanel, InventoryPanel, ProductsPanel } from "./panels/metricPanels";
import { ConnectorsPanel, SystemHealthPanel, AlertsPanel, ApprovalsPanel, ActivityPanel, OmadaPanel, AegisPanel, LiveVersePanel, ContractPanel, AdminDeepLink } from "./panels/opsPanels";
import { OfficesPanel, TasksPanel, OfficeResponsesPanel } from "./panels/tegobPanels";

const SCOPE_LABELS = { PLATFORM: "Platform", MARKETPLACE: "Marketplace", SELLER_ORGANIZATION: "Seller Org", STORE: "Store" };
const FLAT_ITEMS = NAV_GROUPS.flatMap((g) => g.items.map((it) => ({ ...it, groupId: g.id })));

export default function CommandCenter() {
  const navigate = useNavigate();
  const [boot, setBoot] = useState(null);
  const [adminPerms, setAdminPerms] = useState(null);
  const [fatal, setFatal] = useState(null);
  const [activeId, setActiveId] = useState("overview");
  const [scope, setScope] = useState({ scope: "PLATFORM", marketplace_id: "", seller_org_id: "", store_id: "" });
  const [range, setRange] = useState({ date_from: "", date_to: "" });
  const [collapsed, setCollapsed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState(() => Object.fromEntries(NAV_GROUPS.map((g) => [g.id, true])));
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [alertCount, setAlertCount] = useState(0);

  // Auth bootstrap — reuses the admin token; no second auth system.
  useEffect(() => {
    if (!ccToken()) { navigate("/admin/login"); return; }
    let alive = true;
    ccClient().get("/bootstrap")
      .then(({ data }) => { if (alive) setBoot(data?.data || null); })
      .catch((e) => {
        const info = ccErrorInfo(e);
        if (!alive) return;
        if (info?.status === 401) { navigate("/admin/login"); return; }
        setFatal(info);
      });
    adminClient().get("/admin/whoami")
      .then(({ data }) => { if (alive) setAdminPerms(new Set(data?.permissions || [])); })
      .catch(() => { if (alive) setAdminPerms(new Set()); });
    return () => { alive = false; };
  }, [navigate]);

  const flags = boot?.featureFlags || {};
  const caps = useMemo(() => new Set(boot?.capabilities || []), [boot]);
  const canAck = caps.has("command_center.alert.acknowledge");
  const allowedScopes = boot?.allowedScopes || ["PLATFORM"];

  // Notification count from the truthful alert store.
  useEffect(() => {
    if (!boot) return;
    ccClient().get("/alerts").then(({ data }) => {
      const a = data?.data?.alerts || [];
      setAlertCount(a.filter((x) => !x.acknowledged_at).length);
    }).catch(() => {});
  }, [boot]);

  const activeItem = useMemo(() => FLAT_ITEMS.find((i) => i.id === activeId) || FLAT_ITEMS[0], [activeId]);

  const openAdmin = useCallback((section) => { navigate(`/admin?section=${section}`); }, [navigate]);

  const runSearch = useCallback(async (term) => {
    if (!term.trim()) { setResults(null); return; }
    try {
      const { data } = await ccClient().get("/search", { params: { q: term, scope: scope.scope } });
      setResults(data?.data?.results || []);
    } catch (e) { setResults([]); }
  }, [scope.scope]);

  const logout = () => { localStorage.removeItem("taven_admin_token"); navigate("/admin/login"); };

  if (fatal) {
    return (
      <div className="min-h-screen bg-[#070a12] text-white flex flex-col items-center justify-center gap-4 px-6 text-center" data-testid="cc-fatal">
        <ShieldCheck className="w-10 h-10 text-red-400" />
        <h1 className="text-xl font-black">{fatal.code}</h1>
        <p className="text-sm text-zinc-400 max-w-md">{fatal.message}</p>
        <button onClick={() => navigate("/admin")} className="bg-[#C5A059] text-black px-4 py-2 rounded-lg text-sm font-bold" data-testid="cc-fatal-back">Back to Admin</button>
      </div>
    );
  }

  if (!boot) {
    return <div className="min-h-screen bg-[#070a12] text-zinc-400 flex items-center justify-center" data-testid="cc-boot-loading">Loading Command Center…</div>;
  }

  const renderPanel = () => {
    const it = activeItem;
    if (it.type === "admin") return <AdminDeepLink item={it} adminPerms={adminPerms} onOpenAdmin={openAdmin} />;
    if (it.type === "contract") {
      const status = resolveItemStatus(it, flags, adminPerms);
      // A contract with a real panel renders live only when its feature flag is ACTIVE.
      if (it.panelKey === "aegis" && status === "ACTIVE") return <AegisPanel scope={scope} range={range} />;
      if (it.panelKey === "liveverse" && status === "ACTIVE") return <LiveVersePanel scope={scope} range={range} />;
      if (it.panelKey === "tegob-offices" && status === "ACTIVE") return <OfficesPanel />;
      if (it.panelKey === "tegob-tasks" && status === "ACTIVE") return <TasksPanel />;
      if (it.panelKey === "tegob-responses" && status === "ACTIVE") return <OfficeResponsesPanel />;
      return <ContractPanel item={it} flags={flags} adminPerms={adminPerms} onOpenAdmin={openAdmin} />;
    }
    const common = { scope, range };
    switch (it.panelKey) {
      case "overview": return <OverviewPanel {...common} onNavigate={setActiveId} onOpenAdmin={openAdmin} />;
      case "commerce": return <CommercePanel {...common} />;
      case "financial": return <FinancialPanel {...common} />;
      case "orders": return <OrdersPanel {...common} />;
      case "inventory": return <InventoryPanel {...common} />;
      case "products": return <ProductsPanel {...common} />;
      case "connectors": return <ConnectorsPanel {...common} />;
      case "system-health": return <SystemHealthPanel {...common} />;
      case "alerts": return <AlertsPanel {...common} canAck={canAck} />;
      case "approvals": return <ApprovalsPanel {...common} />;
      case "activity": return <ActivityPanel {...common} />;
      case "omada": return <OmadaPanel {...common} />;
      default: return null;
    }
  };

  const NavList = () => (
    <nav className="p-3 space-y-1.5" aria-label="Command Center navigation">
      {NAV_GROUPS.map((g) => {
        const Icon = g.icon;
        const isOpen = openGroups[g.id];
        return (
          <div key={g.id}>
            <button
              onClick={() => setOpenGroups((s) => ({ ...s, [g.id]: !s[g.id] }))}
              data-testid={`cc-group-${g.id}`}
              aria-expanded={isOpen}
              className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-zinc-300 hover:bg-white/5 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#C5A059] transition-colors">
              <Icon className="w-4 h-4 text-[#C5A059] shrink-0" />
              {!collapsed && <span className="text-[11px] font-bold uppercase tracking-wider text-left flex-1">{g.label}</span>}
              {!collapsed && (isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />)}
            </button>
            {!collapsed && isOpen && (
              <div className="ml-3 pl-3 border-l border-white/10 mt-1 space-y-0.5">
                {g.items.map((it) => {
                  const status = resolveItemStatus(it, flags, adminPerms);
                  const active = activeId === it.id;
                  return (
                    <button key={it.id}
                      onClick={() => { setActiveId(it.id); setDrawerOpen(false); }}
                      data-testid={`cc-nav-${it.id}`}
                      className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md text-left text-xs transition-colors focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#C5A059] ${active ? "bg-[#C5A059] text-black font-bold" : "text-zinc-400 hover:bg-white/5 hover:text-white"}`}>
                      <span className="truncate">{it.label}</span>
                      {status !== "ACTIVE" && !active && <span className="shrink-0"><ModuleStatusBadge status={status} /></span>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen bg-[#070a12] text-white" data-testid="command-center">
      <Toaster position="top-right" theme="dark" />
      {/* Top bar */}
      <header className="sticky top-0 z-40 h-16 bg-[#0b0f1a] border-b border-white/10 flex items-center gap-3 px-3 md:px-5">
        <button onClick={() => setDrawerOpen(true)} className="md:hidden p-2 text-zinc-300 hover:text-white" data-testid="cc-drawer-open" aria-label="Open navigation">
          <Menu className="w-5 h-5" />
        </button>
        <button onClick={() => setCollapsed((c) => !c)} className="hidden md:block p-2 text-zinc-400 hover:text-white" data-testid="cc-collapse-toggle" aria-label="Toggle sidebar">
          {collapsed ? <PanelLeftOpen className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />}
        </button>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-base font-black tracking-tight">TAVEN</span>
          <span className="text-[10px] text-[#C5A059] uppercase tracking-[0.2em] hidden sm:block">Command Center</span>
        </div>
        {/* Global search */}
        <div className="relative flex-1 max-w-md mx-auto hidden sm:block">
          <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-full h-9 px-3 focus-within:border-[#C5A059]/50">
            <Search className="w-4 h-4 text-zinc-500" />
            <input value={query} onChange={(e) => { setQuery(e.target.value); }}
              onKeyDown={(e) => { if (e.key === "Enter") runSearch(query); }}
              onBlur={() => setTimeout(() => setResults(null), 200)}
              placeholder="Search users, products…" data-testid="cc-global-search"
              className="bg-transparent text-sm text-white placeholder:text-zinc-500 flex-1 outline-none" />
          </div>
          {results && (
            <div className="absolute top-11 left-0 right-0 bg-[#0f1420] border border-white/10 rounded-lg shadow-xl max-h-80 overflow-auto z-50" data-testid="cc-search-results">
              {results.length === 0 ? <p className="p-4 text-xs text-zinc-500">No results</p> : results.map((r, i) => (
                <div key={i} className="px-4 py-2.5 border-b border-white/5 last:border-0 hover:bg-white/5">
                  <p className="text-sm text-white truncate">{r.label}</p>
                  <p className="text-[10px] text-zinc-500 uppercase tracking-wider">{r.type}</p>
                </div>
              ))}
            </div>
          )}
        </div>
        {/* Scope selector */}
        <div className="hidden lg:flex items-center gap-2">
          <select value={scope.scope} onChange={(e) => setScope((s) => ({ ...s, scope: e.target.value }))}
            data-testid="cc-scope-select"
            className="bg-white/5 border border-white/10 rounded-md text-xs text-white px-2 py-1.5 outline-none focus:border-[#C5A059]/50">
            {["PLATFORM", "MARKETPLACE", "SELLER_ORGANIZATION", "STORE"].map((sc) => (
              <option key={sc} value={sc} disabled={!allowedScopes.includes(sc)} className="bg-[#0b0f1a]">
                {SCOPE_LABELS[sc]}{allowedScopes.includes(sc) ? "" : " (restricted)"}
              </option>
            ))}
          </select>
        </div>
        {/* Notifications */}
        <button onClick={() => setActiveId("alerts")} className="relative p-2 text-zinc-300 hover:text-white" data-testid="cc-notifications" aria-label="Alerts">
          <Bell className="w-5 h-5" />
          {alertCount > 0 && <span className="absolute top-1 right-1 bg-[#F26522] text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">{alertCount}</span>}
        </button>
        {/* Actor */}
        <div className="flex items-center gap-2 pl-2 border-l border-white/10">
          <div className="hidden md:block text-right">
            <p className="text-xs font-bold text-white truncate max-w-[140px]">{boot.actor?.email}</p>
            <p className="text-[9px] text-[#C5A059] uppercase tracking-wider truncate max-w-[140px]">{(boot.actor?.roles || []).join(", ")}</p>
          </div>
          <button onClick={() => navigate("/admin")} className="p-2 text-zinc-400 hover:text-white" title="Back to Admin" data-testid="cc-back-admin" aria-label="Back to Admin">
            <LayoutDashboard className="w-4 h-4" />
          </button>
          <button onClick={logout} className="p-2 text-zinc-400 hover:text-white" title="Logout" data-testid="cc-logout" aria-label="Logout">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      {boot.demoMode && (
        <div className="bg-[#F26522]/15 border-b border-[#F26522]/40 text-[#F8b58c] text-xs text-center py-1.5 font-bold uppercase tracking-wider" data-testid="cc-demo-banner">
          Demo mode — scoped demo actors active
        </div>
      )}

      <div className="flex">
        {/* Desktop sidebar */}
        <aside className={`hidden md:block shrink-0 border-r border-white/10 bg-[#0b0f1a] transition-all duration-200 ${collapsed ? "w-16" : "w-72"}`}
          style={{ minHeight: "calc(100vh - 4rem)" }} data-testid="cc-sidebar">
          <div className="sticky top-16 max-h-[calc(100vh-4rem)] overflow-y-auto"><NavList /></div>
        </aside>

        {/* Mobile drawer */}
        {drawerOpen && (
          <div className="md:hidden fixed inset-0 z-50" data-testid="cc-drawer">
            <div className="absolute inset-0 bg-black/60" onClick={() => setDrawerOpen(false)} />
            <div className="absolute left-0 top-0 bottom-0 w-72 bg-[#0b0f1a] border-r border-white/10 overflow-y-auto">
              <div className="flex items-center justify-between p-3 border-b border-white/10">
                <span className="text-sm font-black">TAVEN Command Center</span>
                <button onClick={() => setDrawerOpen(false)} data-testid="cc-drawer-close" aria-label="Close navigation"><X className="w-5 h-5 text-zinc-400" /></button>
              </div>
              <NavList />
            </div>
          </div>
        )}

        {/* Main content */}
        <main className="flex-1 min-w-0 p-4 md:p-6" data-testid="cc-main">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <div>
              <h1 className="text-xl md:text-2xl font-black tracking-tight">{activeItem.label}</h1>
              <p className="text-xs text-zinc-500 mt-0.5">Scope: {SCOPE_LABELS[scope.scope]} · Ecosystem {boot.ecosystemId}</p>
            </div>
            {/* Date range (applies to metric panels) */}
            <div className="flex items-center gap-2">
              <input type="date" value={range.date_from} onChange={(e) => setRange((r) => ({ ...r, date_from: e.target.value }))}
                data-testid="cc-date-from" className="bg-white/5 border border-white/10 rounded-md text-xs text-white px-2 py-1.5 outline-none focus:border-[#C5A059]/50" />
              <span className="text-zinc-600 text-xs">→</span>
              <input type="date" value={range.date_to} onChange={(e) => setRange((r) => ({ ...r, date_to: e.target.value }))}
                data-testid="cc-date-to" className="bg-white/5 border border-white/10 rounded-md text-xs text-white px-2 py-1.5 outline-none focus:border-[#C5A059]/50" />
            </div>
          </div>

          {/* Lower-scope id inputs (permission-aware; server validates final authority) */}
          {scope.scope !== "PLATFORM" && (
            <div className="flex flex-wrap gap-2 mb-5 p-3 bg-[#0f1420] border border-white/10 rounded-lg" data-testid="cc-scope-ids">
              <input placeholder="marketplace_id" value={scope.marketplace_id} onChange={(e) => setScope((s) => ({ ...s, marketplace_id: e.target.value }))}
                className="bg-white/5 border border-white/10 rounded-md text-xs text-white px-2 py-1.5 outline-none" data-testid="cc-scope-marketplace" />
              {(scope.scope === "SELLER_ORGANIZATION" || scope.scope === "STORE") && (
                <input placeholder="seller_org_id" value={scope.seller_org_id} onChange={(e) => setScope((s) => ({ ...s, seller_org_id: e.target.value }))}
                  className="bg-white/5 border border-white/10 rounded-md text-xs text-white px-2 py-1.5 outline-none" data-testid="cc-scope-org" />
              )}
              {scope.scope === "STORE" && (
                <input placeholder="store_id" value={scope.store_id} onChange={(e) => setScope((s) => ({ ...s, store_id: e.target.value }))}
                  className="bg-white/5 border border-white/10 rounded-md text-xs text-white px-2 py-1.5 outline-none" data-testid="cc-scope-store" />
              )}
              <span className="text-[10px] text-zinc-500 self-center">Client scope never grants authority — validated server-side.</span>
            </div>
          )}

          {renderPanel()}
        </main>
      </div>
    </div>
  );
}
